import { signal, computed } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Table } from 'primeng/table';
import { HttpClient } from '@angular/common/http';
import { ToastService } from 'src/app/toast/toast.service';
import { ConfirmationService } from 'primeng/api';
import { ClientPackageMappingService } from '../services/client-package-mapping.service';
import  moment from 'moment';

export interface ClientPackageMapping {
  id: number;
  clientId: string;
  name: string;
  packageId: string;
  packageName: string;
  startDate?: string; // ISO
  expirationDate?: string;   // ISO
}

export class ClientPackageMappingViewModel {
  private messageService: ToastService;
  private confirmationService: ConfirmationService;
  private fb: FormBuilder;
  ClientPackageMappingService: ClientPackageMappingService;

  constructor(
    http: HttpClient,
    messageService: ToastService,
    confirmationService: ConfirmationService,
    fb: FormBuilder
  ) {
    this.messageService = messageService;
    this.ClientPackageMappingService = new ClientPackageMappingService(http);
    this.confirmationService = confirmationService;
    this.fb = fb;
    this.initializeForms();
  }

  clientList: any[] = [];
  packageList: any[] = [];

  clientPackages = signal<ClientPackageMapping[]>([]);

  selectedClientId = signal<string | null>(null);
  selectedPackageId = signal<string | null>(null);

  incompleteAuthProfileClientIds = signal<Set<string>>(new Set());

  dialogVisible = signal<boolean>(false);
  isEdit = signal<boolean>(false);
  editId: number | null = null;
  
  private originalFormValues: any = null;
  
  private formValueChanges = signal<number>(0);
  mappingForm!: FormGroup;

  today = new Date();

  minStartDate = signal<Date>(this.today);
  minExpirationDate = signal<Date>(this.today);
  expirationDisabled = signal<boolean>(true);

  initializeForms(): void {
    this.mappingForm = this.fb.group({
      clientId: ['', Validators.required],
      packageId: ['', Validators.required],
      startDate: ['', [Validators.required, this.futureDateValidator]],
      expirationDate: ['', Validators.required]
    }, { validators: this.expirationAfterStartValidator });

    this.mappingForm.valueChanges.subscribe(() => {
      this.formValueChanges.update(v => v + 1);
    });

    this.mappingForm.get('startDate')?.valueChanges.subscribe(startDate => {
      if (startDate) {
        const minDate = moment(startDate).toDate();
        this.minExpirationDate.set(minDate);
        this.expirationDisabled.set(false);

        const expirationDate = this.mappingForm.get('expirationDate')?.value;
        if (expirationDate) {
          if (moment(expirationDate).isSameOrBefore(moment(startDate), 'day')) {
            this.mappingForm.get('expirationDate')?.setValue(null);
          }
        } 
      } else {
        this.minExpirationDate.set(this.today);
        this.expirationDisabled.set(true);
        this.mappingForm.get('expirationDate')?.setValue(null);
      }
    });
  }

  // Custom validator: start date must be today or future
  private futureDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const selectedDate = moment(control.value).startOf('day');
    const today = moment().startOf('day');

    return selectedDate.isSameOrAfter(today) ? null : { pastDate: true };
  }

  // Custom validator: expiration date must be after start date
  private expirationAfterStartValidator(group: AbstractControl): ValidationErrors | null {
    const startDate = group.get('startDate')?.value;
    const expirationDate = group.get('expirationDate')?.value;

    if (!startDate || !expirationDate) return null;

    const start = moment(startDate).startOf('day');
    const expiration = moment(expirationDate).startOf('day');

    return expiration.isAfter(start) ? null : { expirationBeforeStart: true };
  }

  cols = [
    { field: 'name', header: 'Client' },
    { field: 'packageName', header: 'Package' }
  ];

  filteredMappings = computed(() => {
    let data = this.clientPackages();
    const c = this.selectedClientId();
    const p = this.selectedPackageId();
    if (c) data = data.filter(d => d.clientId === c);
    if (p) data = data.filter(d => d.packageId === p);
    return data;
  });

  onGlobalFilter(table: Table, event: Event): void {
    const target = event.target as HTMLInputElement;
    table.filterGlobal(target.value, 'contains');
  }

  openNew(): void {
    this.isEdit.set(false);
    this.editId = null;
    this.originalFormValues = null;
    this.mappingForm.reset();
    this.expirationDisabled.set(true);
    this.minExpirationDate.set(this.today);
    this.minStartDate.set(this.today);
    this.mappingForm.get('startDate')?.setValidators([Validators.required, this.futureDateValidator]);
    this.mappingForm.get('startDate')?.updateValueAndValidity();
    // Enable client and package fields for new entries
    this.mappingForm.get('clientId')?.enable();
    this.mappingForm.get('packageId')?.enable();
    this.dialogVisible.set(true);
  }

  private parseDateString(dateStr: string | undefined): Date | null {
    if (!dateStr) return null;
    return moment(dateStr, 'YYYY-MM-DD').toDate();
  }

  editMapping(row: ClientPackageMapping): void {
    this.isEdit.set(true);
    this.editId = row.id;

    const startDate = this.parseDateString(row.startDate);
    const expirationDate = this.parseDateString(row.expirationDate);

    // Store original values for comparison
    this.originalFormValues = {
      clientId: row.clientId,
      packageId: row.packageId,
      startDate: startDate,
      expirationDate: expirationDate
    };

    // In edit mode, allow selecting from the earlier of (original date or today)
    const today = moment().startOf('day');
    
    if (startDate) {
      const startDateMoment = moment(startDate).startOf('day');
      
      // Use the EARLIER date between original start date and today
      const minDate = startDateMoment.isBefore(today, 'day') ? startDateMoment.toDate() : today.toDate();
      this.minStartDate.set(minDate);
      this.minExpirationDate.set(minDate);
      this.expirationDisabled.set(false);
    } else {
      this.minStartDate.set(today.toDate());
      this.minExpirationDate.set(today.toDate());
    }

    // Remove future date validation for editing
    this.mappingForm.get('startDate')?.clearValidators();
    this.mappingForm.get('startDate')?.setValidators([Validators.required]);
    this.mappingForm.get('startDate')?.updateValueAndValidity({ emitEvent: false });

    this.mappingForm.patchValue({
      clientId: row.clientId,
      packageId: row.packageId,
      startDate: startDate,
      expirationDate: expirationDate
    }, { emitEvent: false });

    // Disable client and package fields in edit mode (cannot change the assignment identity)
    this.mappingForm.get('clientId')?.disable();
    this.mappingForm.get('packageId')?.disable();

    // Manually trigger validation after patching
    this.mappingForm.updateValueAndValidity();

    this.dialogVisible.set(true);
  }

  deleteMapping(row: any) {
    const data = row.id
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the mapping for ${row.client?.name} - ${row.package?.packageName}?`,
      header: 'Confirm Delete',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-warn',
      accept: () => {
        this.ClientPackageMappingService.deleteClientPackageMapping(data).subscribe({
          next: () => {
            this.loadClientPackages();
            this.messageService.success('Client package mapping deleted successfully');
          },
          error: (err) => {
            this.messageService.error('Failed to delete client package mapping');
          }
        });
      }
    });
  }

  // Computed signal to check if form has changes
  hasFormChanges = computed(() => {
    // Trigger reactivity by reading the signal
    this.formValueChanges();
    
    if (!this.isEdit() || !this.originalFormValues) {
      return true; // Always allow submit for new records
    }
    
    const currentValues = this.mappingForm.getRawValue();
    
    // Compare dates using moment
    const currentStartDate = currentValues.startDate ? moment(currentValues.startDate).format('YYYY-MM-DD') : null;
    const originalStartDate = this.originalFormValues.startDate ? moment(this.originalFormValues.startDate).format('YYYY-MM-DD') : null;
    const currentExpirationDate = currentValues.expirationDate ? moment(currentValues.expirationDate).format('YYYY-MM-DD') : null;
    const originalExpirationDate = this.originalFormValues.expirationDate ? moment(this.originalFormValues.expirationDate).format('YYYY-MM-DD') : null;
    
    return (
      currentValues.clientId !== this.originalFormValues.clientId ||
      currentValues.packageId !== this.originalFormValues.packageId ||
      currentStartDate !== originalStartDate ||
      currentExpirationDate !== originalExpirationDate
    );
  });
  
  // Computed signal for submit button disabled state
  isSubmitDisabled = computed(() => {
    // Trigger reactivity
    this.formValueChanges();
    
    return this.mappingForm.invalid || (this.isEdit() && !this.hasFormChanges());
  });

  hideDialog(): void {
    this.dialogVisible.set(false);
    this.originalFormValues = null;
  }

  init(): void {
    this.loadClientPackages();
    this.loadClientList();
    this.loadPackageList();
    this.loadIncompleteAuthProfiles();
  }

  hasIncompleteAuthProfiles(clientId: string): boolean {
    return this.incompleteAuthProfileClientIds().has(clientId);
  }

  private loadIncompleteAuthProfiles(): void {
    this.ClientPackageMappingService.getIncompleteAuthProfileClients().subscribe({
      next: (clientIds: string[]) => {
        this.incompleteAuthProfileClientIds.set(new Set(clientIds));
      },
      error: () => {
        this.incompleteAuthProfileClientIds.set(new Set());
      }
    });
  }

  private loadClientPackages(): void {
    this.ClientPackageMappingService.getAllClientPackageMappings().subscribe({
      next: (data) => {
        this.clientPackages.set(data);
      },
      error: (err) => {
        this.messageService.error('Failed to load client package mappings');
      }
    });
  }

  private loadClientList(): void {
    this.ClientPackageMappingService.getAllClients().subscribe({
      next: (response: any) => {
        const list = response?.data || [];
        const mapped = list.map((client: any) => ({
          name: client.name,
          code: client.id
        }));
        this.clientList = mapped;
      },
      error: (err) => {
        this.clientList = [];
      }
    });
  }

  private loadPackageList(): void {
    this.ClientPackageMappingService.getAllPackages().subscribe({
      next: (list: any) => {
        const mapped = list.map((pkg: any) => ({
          name: pkg.packageName,
          code: pkg.id
        }));
        this.packageList = mapped;
      },
      error: (err) => {
        this.packageList = [];
      }
    });
  }

  submitDialog(): void {
    Object.keys(this.mappingForm.controls).forEach(key => {
      this.mappingForm.get(key)?.markAsTouched();
    });

    if (this.mappingForm.invalid) return;

    // Use getRawValue() to include disabled fields (clientId and packageId in edit mode)
    const formValue = this.mappingForm.getRawValue();

    const payload: any = {
      clientId: formValue.clientId,
      packageId: formValue.packageId,
      startDate: moment(formValue.startDate).format('YYYY-MM-DD'),
      expirationDate: moment(formValue.expirationDate).format('YYYY-MM-DD'),
    };

    if (this.editId) {
      payload.id = this.editId;
    }

    const isEdit = this.isEdit();
    const action = isEdit ? 'updated' : 'created';

    this.ClientPackageMappingService.saveClientPackageMapping(payload).subscribe({
      next: (result) => {
        if (isEdit) {
          this.clientPackages.set(this.clientPackages().map(x => x.id === this.editId ? result : x));
        } else {
          this.clientPackages.set([...this.clientPackages(), result]);
        }
        this.messageService.success(`Client Package mapping ${action} successfully`);
        this.hideDialog();
        this.loadClientPackages();
        if (!isEdit) {
          this.loadIncompleteAuthProfiles();
        }
      },
      error: (err) => {
        this.messageService.error(`Active Client Package association already exists`);
      }
    });
  }
}
